import React, { useEffect, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import AiSearchIcon from '@atlaskit/icon-lab/core/ai-search';
import { usePublish } from '@atlaskit/rovo-triggers/main';

import { useAISmartButtonActions } from '@atlassian/ai-smart-button-api';
import { HandleActionType } from '@atlassian/rovo-conversation-actions';

import type {
	RovoConfiguration,
	RovoConfigurationProviderProps,
	SearchPageRovoConfigProviderProps,
} from '@confluence/rovo-config-provider-common/entry-points/rovoConfigTypes.ts';
import { useRovoConfigProviderProps } from '@confluence/rovo-config-provider-common/entry-points/useRovoConfigProviderProps.ts';
import {
	type GetSearchPageRovoActions,
	useSearchPageRovoActions,
} from '@confluence/search-page-rovo-config-common/entry-points/useSearchPageRovoActions.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import iconDark from './assets/search-icon-colorized-dark.svg';
import iconLight from './assets/search-icon-colorized-light.svg';

const messages = defineMessages({
	searchAssistanceGreetingTitle: {
		id: 'advanced-search.rovo-config-provider.search-assistance-greeting-title',
		defaultMessage: 'Can I help you find something{name, select, undefined {} other {, {name}}}?',
		description: 'Title of the search assistance greeting.',
	},
	searchAssistanceNudgeSubheaderLabel: {
		id: 'advanced-search.rovo-config-provider.search-assistance-nudge-subheader',
		defaultMessage: 'Search across your work to find what you need',
		description: 'Label for the search assistance nudge subheader',
	},
});

const EMPTY_ROVO_CONFIG: RovoConfiguration = {
	getActions: async () => [],
};

export const AdvancedSearchRovoConfigProvider = ({
	setConfiguration,
}: RovoConfigurationProviderProps) => {
	const props = useRovoConfigProviderProps() as SearchPageRovoConfigProviderProps;
	const {
		getSearchQuery,
		shouldUseSearchPageRovoConfig,
		shouldNudgeToQueryStarters,
		shouldNudgeToSummarizeQuery,
	} = props;
	const query = (getSearchQuery?.() ?? '').toString();
	const getSearchPageRovoActions: GetSearchPageRovoActions = useSearchPageRovoActions(query);
	const { displayName } = useSessionData();
	const { setNudges } = useAISmartButtonActions();
	const publishRovo = usePublish('ai-mate');

	const intl = useIntl();

	// Use first name for greeting when available; fallback for anonymous or missing displayName
	const firstName = displayName?.split(' ')[0];
	const title = intl.formatMessage(messages.searchAssistanceGreetingTitle, {
		name: firstName ?? undefined,
	});

	// When undefined (e.g. prop not passed), use empty config for backward compatibility
	const useSearchPageConfig = shouldUseSearchPageRovoConfig ?? false;

	const config = useMemo<RovoConfiguration>(
		() =>
			useSearchPageConfig && query !== ''
				? {
						getActions: async () => {
							const actionEntries = getSearchPageRovoActions();
							const actions = actionEntries.map(({ action }) => action);
							return Promise.resolve(actions);
						},
						customGreeting: {
							title,
							iconSrc: iconLight,
							iconSrcDark: iconDark,
							truncateGreeting: false,
						},
						prioritizeCustomActionResults: true,
						shouldShowGreeting: true,
					}
				: EMPTY_ROVO_CONFIG,
		[useSearchPageConfig, getSearchPageRovoActions, title, query],
	);

	useEffect(() => {
		setConfiguration(config);

		//If user is in the nudge to query starters cohort, set the nudge to query starters action
		if (shouldNudgeToQueryStarters) {
			setNudges([
				{
					id: 'nudge-to-query-starters-ai-action',
					title,
					secondaryTitle: intl.formatMessage(messages.searchAssistanceNudgeSubheaderLabel),
					icon: () => <AiSearchIcon label="" />,
					onClick: () => {
						publishRovo({
							type: 'chat-new',
							source: 'advanced-search-confluence',
							openChat: true,
							openChatMode: 'mini-modal',
							data: { dialogues: [] },
						});
					},
					handleType: HandleActionType.Base,
					type: 'ai-action',
					nudgeContext: {
						id: 'nudge-to-query-starters',
					},
				},
			]);
		}
		//If user is in the nudge to summarize query cohort, set the summarize top results action
		if (shouldNudgeToSummarizeQuery) {
			const actionsEntries = getSearchPageRovoActions();
			const actions = actionsEntries.map(({ action }) => action);
			const summarizeTopResultsAction = actions.find(
				(a: { id: string }) => a.id === 'summarize-top-results-action',
			);
			const transformedAction = summarizeTopResultsAction
				? {
						...summarizeTopResultsAction,
						secondaryTitle: intl.formatMessage(messages.searchAssistanceNudgeSubheaderLabel),
						nudgeContext: {
							id: 'nudge-to-summarize-query',
						},
					}
				: null;
			setNudges(transformedAction ? [transformedAction] : []);
		}
	}, [
		config,
		setConfiguration,
		shouldNudgeToQueryStarters,
		shouldNudgeToSummarizeQuery,
		getSearchPageRovoActions,
		setNudges,
		intl,
		publishRovo,
		title,
	]);

	return null;
};
